import Papa from 'papaparse';
import { useState } from 'react';

import { apiRequest } from '@/config';

import { Button } from '../ui/Button';
import { InputField, TextareaField } from '../ui/FormField';
import { Modal } from '../ui/Modal';

interface CSVImporterProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface CSVRow {
  prompt: string;
  category?: string;
  difficulty?: string;
  notes?: string;
}

export function CSVImporter({ onClose, onSuccess }: CSVImporterProps) {
  const [csvText, setCsvText] = useState('');
  const [createSet, setCreateSet] = useState(true);
  const [setName, setSetName] = useState('');
  const [setDescription, setSetDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<CSVRow[]>([]);

  const parseCSV = (text: string): CSVRow[] => {
    // Use PapaParse to handle proper CSV parsing (quoted fields, escaping, etc.)
    const result = Papa.parse<CSVRow>(text, {
      header: true,
      skipEmptyLines: true,
      transformHeader: header => header.trim().toLowerCase(),
      transform: value => value.trim() || undefined,
    });

    // Check for parsing errors
    if (result.errors.length > 0) {
      const error = result.errors[0];
      throw new Error(`CSV parse error on row ${error.row}: ${error.message}`);
    }

    // Validate we have data
    if (result.data.length === 0) {
      throw new Error('CSV must have at least one data row');
    }

    // Validate required 'prompt' column exists
    const firstRow = result.data[0];
    if (!firstRow || !('prompt' in firstRow) || !firstRow.prompt) {
      throw new Error("CSV must have a 'prompt' column with values");
    }

    return result.data;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = event => {
      const text = event.target?.result as string;
      setCsvText(text);

      try {
        const parsed = parseCSV(text);
        setPreview(parsed);
        setError('');
      } catch (err: any) {
        setError(err.message);
        setPreview([]);
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (preview.length === 0) {
        throw new Error('No valid rows to import');
      }

      if (createSet && !setName) {
        throw new Error('Set name is required when creating a prompt set');
      }

      const data = await apiRequest('/api/eval/prompts/import-csv', {
        method: 'POST',
        body: JSON.stringify({
          rows: preview,
          create_set: createSet,
          set_name: setName,
          set_description: setDescription,
        }),
      });

      alert(`Successfully imported ${data.imported_count} prompts!`);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Import Prompts from CSV" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {error && (
          <div
            style={{
              padding: '0.75rem',
              background: '#fee',
              borderRadius: '4px',
              color: '#c00',
            }}
          >
            {error}
          </div>
        )}

        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 500,
            }}
          >
            Upload CSV File
          </label>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ width: '100%' }}
          />
          <p
            style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}
          >
            Required columns: <strong>prompt</strong>
            <br />
            Optional columns: category, difficulty, notes
            <br />
            <em>
              Names will be auto-generated as:
              SetName-Category-Difficulty-Serial
            </em>
            <br />
            <em style={{ fontSize: '0.8125rem', color: '#999' }}>
              Supports quoted fields with commas, multi-line values, and proper
              escaping
            </em>
          </p>
        </div>

        {preview.length > 0 && (
          <div>
            <h4 style={{ marginBottom: '0.5rem' }}>
              Preview ({preview.length} rows)
            </h4>
            <div
              style={{
                maxHeight: '200px',
                overflow: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '0.5rem',
              }}
            >
              {preview.slice(0, 5).map((row, idx) => {
                const baseName = setName || 'Imported';
                const serial = String(idx + 1).padStart(3, '0');
                const parts = [baseName];
                if (row.category) parts.push(row.category);
                if (row.difficulty) parts.push(row.difficulty);
                parts.push(serial);
                const generatedName = parts.join('-');

                return (
                  <div
                    key={idx}
                    style={{
                      marginBottom: '0.5rem',
                      paddingBottom: '0.5rem',
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <strong style={{ fontSize: '0.75rem', color: '#999' }}>
                      {generatedName}
                    </strong>
                    {row.category && (
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          padding: '0.125rem 0.375rem',
                          background: 'rgba(183, 52, 58, 0.1)',
                          color: '#B7343A',
                          borderRadius: '8px',
                          fontSize: '0.625rem',
                        }}
                      >
                        {row.category}
                      </span>
                    )}
                    {row.difficulty && (
                      <span
                        style={{
                          marginLeft: '0.25rem',
                          padding: '0.125rem 0.375rem',
                          background: 'rgba(139, 119, 100, 0.1)',
                          color: '#8B7764',
                          borderRadius: '8px',
                          fontSize: '0.625rem',
                        }}
                      >
                        {row.difficulty}
                      </span>
                    )}
                    <br />
                    <span style={{ fontSize: '0.875rem', color: '#666' }}>
                      {row.prompt.substring(0, 100)}...
                    </span>
                  </div>
                );
              })}
              {preview.length > 5 && (
                <div style={{ color: '#666', fontSize: '0.875rem' }}>
                  ... and {preview.length - 5} more
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <input
              type="checkbox"
              checked={createSet}
              onChange={e => setCreateSet(e.target.checked)}
            />
            Create a prompt set from these prompts
          </label>
        </div>

        {createSet && (
          <>
            <InputField
              label="Set Name"
              value={setName}
              onChange={e => setSetName(e.target.value)}
              placeholder="e.g., Q1 2025 Test Cases"
              required
            />
            <TextareaField
              label="Set Description"
              value={setDescription}
              onChange={e => setSetDescription(e.target.value)}
              placeholder="Optional description"
            />
          </>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            marginTop: '1rem',
          }}
        >
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || preview.length === 0}
            loading={loading}
          >
            Import {preview.length} Prompt{preview.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
